import { createContext, useContext, useState } from 'react';
import { cn } from 'utils';

// Tab Context
const TabsContext = createContext({});

// Main Tabs Container
export function Tabs({ value, onValueChange, defaultValue, children, className }) {
  const [selectedTab, setSelectedTab] = useState(value || defaultValue || '');

  const handleTabChange = (tabValue) => {
    if (onValueChange) {
      onValueChange(tabValue);
    } else {
      setSelectedTab(tabValue);
    }
  };

  const currentTab = value ?? selectedTab;

  return (
    <TabsContext.Provider value={{ selectedTab: currentTab, onTabChange: handleTabChange }}>
      <div className={cn('w-full', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// Tab List (Navigation)
export function TabsList({ children, className }) {
  return (
    <div className={cn(
      'flex items-center border-b border-gray-200 dark:border-dark-600 bg-transparent p-0 text-gray-500 dark:text-dark-300 w-full h-auto',
      className
    )}>
      {children}
    </div>
  );
}

// Individual Tab Trigger
export function TabsTrigger({ value, children, className, disabled }) {
  const { selectedTab, onTabChange } = useContext(TabsContext);
  const isSelected = selectedTab === value;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onTabChange(value)}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap px-5 py-3 text-sm font-semibold ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-b-2 -mb-px',
        isSelected
          ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
          : 'border-transparent text-gray-500 dark:text-dark-300 hover:text-gray-700 dark:hover:text-dark-100 hover:border-gray-300 dark:hover:border-dark-500',
        className
      )}
    >
      {children}
    </button>
  );
}

// Tab Content Container
export function TabsContent({ value, children, className }) {
  const { selectedTab } = useContext(TabsContext);
  
  if (selectedTab !== value) {
    return null;
  }

  return (
    <div className={cn('mt-6 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2', className)}>
      {children}
    </div>
  );
}

// Export everything as default for easier imports
export default {
  Root: Tabs,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
};